import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  VStack,
  Text,
  Badge,
  Divider,
  Flex,
  // Avatar, // Removing unused import
  useToast,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputRightElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { EmailIcon, DeleteIcon, SearchIcon } from '@chakra-ui/icons';
import { deleteMessage } from '../../Redux/Login/action';

// Message bubble component
const MessageBubble = ({ message, handleDelete }) => {
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = React.useState(false);
  const cancelRef = React.useRef();
  
  // Determine if admin message
  const isAdminMessage = message.messageType === 'admin';
  
  return (
    <Box width="100%" mb={4}>
      <Flex justify="space-between" mb={1}>
        <Flex align="center">
          <Text fontSize="sm" fontWeight="bold">To: {message.toBurrowerId}</Text>
          {isAdminMessage && <Badge ml={2} colorScheme="purple">Admin Message</Badge>}
        </Flex>
        <Text fontSize="xs" color="gray.500">
          {message.formattedTime}
        </Text>
      </Flex>
      
      <Flex justify="flex-end">
        <Box 
          bg="teal.900" 
          p={3} 
          borderRadius="lg" 
          borderTopRightRadius="sm"
          maxW="90%"
          position="relative"
          boxShadow="sm"
        >
          <Text color="white">{message.messageContents}</Text>
          
          <Flex mt={2} justify="flex-start" opacity={0.7}>
            <Tooltip label="Delete">
              <IconButton
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => setIsDeleteAlertOpen(true)}
                aria-label="Delete message"
                ml={1}
              />
            </Tooltip>
          </Flex>
        </Box>
      </Flex>
      
      {/* Delete confirmation alert */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteAlertOpen(false)}
        size="sm"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Sent Message
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this message from your sent items? The recipient will still see their copy.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteAlertOpen(false)}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={() => {
                  handleDelete(message.sentMessageId);
                  setIsDeleteAlertOpen(false);
                }}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

const SentMessagesModal = ({ isOpen, onClose, messages = [] }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { sentMessagesLoading } = useSelector(state => state.auth.messages || {});
  // Using setDeletingId but keeping track of which message is being deleted
  const [deletingId, setDeletingId] = useState(null);
  const [localMessages, setLocalMessages] = useState(messages);
  const [searchQuery, setSearchQuery] = useState('');

  React.useEffect(() => {
    setLocalMessages(messages);
  }, [messages]);
  
  // Format time ago function (replacement for date-fns formatDistanceToNow)
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    // Time intervals in seconds
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
      second: 1
    };
    
    let counter;
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      counter = Math.floor(seconds / secondsInUnit);
      if (counter > 0) {
        return counter === 1 ? `1 ${unit} ago` : `${counter} ${unit}s ago`;
      }
    }
    
    return 'just now';
  };
  
  // Process messages with formatted time
  const processedMessages = localMessages.map(msg => ({
    ...msg,
    formattedTime: formatTimeAgo(msg.sentAt)
  }));
  
  // Sort messages by date, newest first
  const sortedMessages = [...processedMessages].sort((a, b) => 
    new Date(b.sentAt) - new Date(a.sentAt)
  );
  
  // Filter messages based on search query
  const filteredMessages = searchQuery 
    ? sortedMessages.filter(msg => 
        (msg.messageContents && msg.messageContents.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (msg.toBurrowerId && msg.toBurrowerId.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : sortedMessages;
  
  const handleDeleteMessage = async (messageId) => {
    setDeletingId(messageId);
    try {
      const result = await dispatch(deleteMessage(messageId, 'sent'));
      
      if (result.success) {
        toast({
          title: "Message deleted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Update local state to remove the message
        setLocalMessages(prev => prev.filter(msg => msg.sentMessageId !== messageId));
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete message",
          status: "error",
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh" bg="gray.800" color="white">
        <ModalHeader>Sent Messages</ModalHeader>
        <ModalCloseButton />
        
        <Box px={6} pb={2}>
          <InputGroup size="md">
            <Input
              placeholder="Search sent messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              pr="4.5rem"
            />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={() => setSearchQuery('')}>
                {searchQuery ? 'Clear' : <SearchIcon />}
              </Button>
            </InputRightElement>
          </InputGroup>
        </Box>
        
        <ModalBody>
          {sentMessagesLoading ? (
            <Flex justify="center" my={4}>
              <Spinner />
            </Flex>
          ) : localMessages.length === 0 ? (
            <Box textAlign="center" py={10}>
              <EmailIcon boxSize="40px" color="gray.400" mb={3} />
              <Text fontSize="lg">No sent messages</Text>
              <Text color="gray.500">Messages you've sent will appear here</Text>
            </Box>
          ) : filteredMessages.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text fontSize="lg">No matching messages</Text>
              <Text color="gray.500">Try a different search term</Text>
              <Button mt={4} size="sm" onClick={() => setSearchQuery('')}>Clear Search</Button>
            </Box>
          ) : (
            <VStack spacing={0} align="stretch" divider={<Divider />}>
              {filteredMessages.map(message => (
                <MessageBubble
                  key={message.sentMessageId}
                  message={message}
                  handleDelete={handleDeleteMessage}
                />
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>  );
};

// Make sure this export is properly included in the final file
export default SentMessagesModal;
